// Stromzaehlerablesung 1.0.6
// Prueft, ob auf diesem Geraet unter den aktuellen Bedingungen eine Erfassung ohne Netz moeglich waere.
import { getCachedReadings, queueCount } from './db';
import type { HostMode } from './types';

export type CheckLevel = 'ok' | 'warn' | 'fail' | 'info';
/** online-only: Teams, Outlook und Browser-Tab arbeiten bewusst nur online (kein Fehler). */
export type ReadinessLevel = 'ready' | 'limited' | 'unavailable' | 'online-only';

export interface ReadinessCheck {
  id: 'mode' | 'worker' | 'login' | 'data' | 'queue' | 'storage' | 'ios';
  title: string;
  level: CheckLevel;
  detail: string;
}

export interface OfflineReadiness {
  level: ReadinessLevel;
  headline: string;
  checks: ReadinessCheck[];
  checkedAt: string;
}

/** Beschriftung des Chips im Kopfbereich */
export const LEVEL_LABEL: Record<ReadinessLevel, string> = {
  ready: 'Offline möglich',
  limited: 'Offline eingeschränkt',
  unavailable: 'Offline nicht möglich',
  'online-only': 'Offline nicht möglich'
};

export const CACHE_INFO_KEY = 'pv-cache-info';

interface CacheInfo { at: string; year: number; count: number }

/** Merkt sich, wann und wie viele Ablesungen zuletzt fuer den Offlinebetrieb auf dem Geraet gespeichert wurden. */
export function rememberCacheInfo(year: number, count: number): void {
  try {
    localStorage.setItem(CACHE_INFO_KEY, JSON.stringify({ at: new Date().toISOString(), year, count }));
  } catch {
    // Speicher nicht verfuegbar: die Anzeige verzichtet dann auf die Zeitangabe.
  }
}

function readCacheInfo(): CacheInfo | undefined {
  try {
    const raw = localStorage.getItem(CACHE_INFO_KEY);
    if (!raw) return undefined;
    const value = JSON.parse(raw) as { at?: unknown; year?: unknown; count?: unknown };
    if (typeof value.at !== 'string') return undefined;
    return { at: value.at, year: Number(value.year), count: Number(value.count) };
  } catch {
    return undefined;
  }
}

function isAppleMobile(): boolean {
  const ua = navigator.userAgent;
  return /iPad|iPhone|iPod/.test(ua) || (ua.includes('Macintosh') && navigator.maxTouchPoints > 1);
}

function formatTime(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' });
}

export async function checkOfflineReadiness(p: { mode: HostMode; year: number; signedIn: boolean }): Promise<OfflineReadiness> {
  const checks: ReadinessCheck[] = [];
  const isPwa = p.mode === 'pwa';
  const checkedAt = new Date().toISOString();

  if (!isPwa) {
    if (p.mode === 'teams' || p.mode === 'outlook') {
      const where = p.mode === 'teams' ? 'In Microsoft Teams' : 'In Microsoft Outlook';
      checks.push({
        id: 'mode', title: 'Onlinebetrieb', level: 'info',
        detail: `${where} arbeitet die App bewusst nur online. Für die Erfassung ohne Netz die installierte App verwenden, die du über das Symbol auf dem Home-Bildschirm startest.`
      });
    } else {
      checks.push({
        id: 'mode', title: 'Onlinebetrieb', level: 'info',
        detail: 'Im Browser-Tab arbeitet die App nur online. Die Erfassung ohne Netz steht in der installierten App zur Verfügung, weil nur dort die lokal gespeicherten Daten zuverlässig erhalten bleiben.'
      });
      checks.push({
        id: 'ios', title: 'App installieren', level: 'info',
        detail: isAppleMobile()
          ? 'iPhone und iPad: In Safari auf „Teilen“ tippen und „Zum Home-Bildschirm“ wählen, danach die App vom Home-Bildschirm öffnen. Ob andere Browser wie Edge die Installation anbieten, kann je nach iOS-Version abweichen.'
          : 'Im Browser-Menü „App installieren“ bzw. „Zum Startbildschirm hinzufügen“ wählen und die App von dort öffnen.'
      });
    }
    return { level: 'online-only', headline: 'Nur Onlinebetrieb', checks, checkedAt };
  }

  checks.push({ id: 'mode', title: 'Installierte App (PWA)', level: 'ok', detail: 'Die App läuft als installierte PWA.' });

  const controlled = 'serviceWorker' in navigator && Boolean(navigator.serviceWorker.controller);
  checks.push(controlled
    ? { id: 'worker', title: 'Start ohne Netz', level: 'ok', detail: 'Die App-Dateien sind für den Start ohne Netz vorbereitet.' }
    : { id: 'worker', title: 'Start ohne Netz', level: 'warn', detail: 'Die App-Dateien sind noch nicht für den Start ohne Netz vorbereitet. Die App einmal ganz schließen und mit Netz neu öffnen.' });

  let cachedUser = false;
  try { cachedUser = Boolean(localStorage.getItem('pv-user')); } catch { cachedUser = false; }
  checks.push(p.signedIn && cachedUser
    ? { id: 'login', title: 'Anmeldung', level: 'ok', detail: 'Anmeldung und Benutzerdaten sind auf diesem Gerät gespeichert.' }
    : { id: 'login', title: 'Anmeldung', level: 'fail', detail: 'Für den Start ohne Netz ist vorher eine Anmeldung mit Netz nötig. Die App mit Netz öffnen und anmelden.' });

  let count = 0;
  try { count = (await getCachedReadings(p.year)).length; } catch { count = 0; }
  const info = readCacheInfo();
  const when = info && info.year === p.year ? `, zuletzt geladen am ${formatTime(info.at)}` : '';
  checks.push(count > 0
    ? { id: 'data', title: `Ablesungen ${p.year}`, level: 'ok', detail: `${count} Ablesung(en) sind ohne Netz verfügbar${when}.` }
    : { id: 'data', title: `Ablesungen ${p.year}`, level: 'fail', detail: `Für ${p.year} sind noch keine Ablesungen auf dem Gerät. Einmal mit Netz „Aktualisieren“ wählen.` });

  let pending = 0;
  try { pending = await queueCount(); } catch { pending = 0; }
  checks.push({
    id: 'queue',
    title: 'Übertragung',
    level: 'info',
    detail: pending > 0
      ? `${pending} erfasste Ablesung(en) warten auf die Übertragung. „Jetzt synchronisieren“ geht nur mit Netz und gültiger Anmeldung.`
      : 'Keine erfasste Ablesung wartet auf die Übertragung. Die Übertragung braucht später Netz und eine gültige Anmeldung.'
  });

  let persisted: boolean | undefined;
  try { persisted = await navigator.storage?.persisted?.(); } catch { persisted = undefined; }
  if (persisted === true) {
    checks.push({ id: 'storage', title: 'Gerätespeicher', level: 'info', detail: 'Der Speicher ist als dauerhaft markiert.' });
  } else if (persisted === false) {
    checks.push({ id: 'storage', title: 'Gerätespeicher', level: 'info', detail: 'Der Speicher ist nicht als dauerhaft markiert. Das Gerät kann lokale Daten bei Platzmangel oder langer Nichtnutzung löschen. Erfasste Ablesungen deshalb zeitnah übertragen.' });
  }

  if (isAppleMobile()) {
    checks.push({ id: 'ios', title: 'iPhone und iPad', level: 'info', detail: 'Die Übertragung läuft nicht im Hintergrund. Nach der Rückkehr ins Netz die App öffnen und „Jetzt synchronisieren“ wählen.' });
  }

  const required = checks.filter(check => check.id === 'login' || check.id === 'data');
  const level: ReadinessLevel = required.some(check => check.level === 'fail')
    ? 'unavailable'
    : checks.some(check => check.level === 'warn') ? 'limited' : 'ready';
  const headline = level === 'ready'
    ? 'Offline-Erfassung ist bereit'
    : level === 'limited' ? 'Offline-Erfassung eingeschränkt' : 'Offline-Erfassung nicht möglich';
  return { level, headline, checks, checkedAt };
}
